<template>
  <div class="modern-building-view">
    <Loader :loading="showLoader" />

    <!-- Modern Header -->
    <div class="modern-header-wrapper">
      <v-toolbar class="modern-toolbar" flat dense elevation="0">
        <div class="toolbar-content">
          <div class="brand-section">
            <div class="logo-wrapper">
              <img :src="logo" class="modern-logo" />
            </div>
            <div class="brand-text">
              <h1 class="workspace-title">{{ $t('office.workspace') }}</h1>
              <p class="workspace-subtitle">{{ $t('office.smart-building') }}</p>
            </div>
          </div>

          <div class="action-buttons">
            <v-btn icon @click="openMapView" variant="text" class="modern-action-btn map-btn">
              <v-icon :color="primaryColor" size="20">mdi-map-outline</v-icon>
            </v-btn>

            <v-btn @click="openBuildingView" :color="primaryColor" variant="elevated" class="modern-primary-btn">
              <v-icon left size="18">mdi-cube-outline</v-icon>
              <span class="btn-text">{{ $t('office.3d-view') }}</span>
            </v-btn>
          </div>
        </div>
      </v-toolbar>
    </div>

    <!-- Building Selection Dialog -->
    <v-dialog v-model="showDialog" persistent max-width="500px" class="modern-dialog">
      <v-card class="dialog-card">
        <div class="dialog-header">
          <div class="dialog-icon">
            <v-icon color="white" size="24">mdi-office-building</v-icon>
          </div>
          <h3 class="dialog-title">{{ $t('office.select-building') }}</h3>
        </div>
        <v-card-text class="dialog-content">
          <v-select
            :items="buildings"
            item-value="name"
            item-title="name"
            :label="$t('office.choose-building')"
            v-model="tempSelectedBuilding"
            return-object
            variant="outlined"
            class="modern-select"
          ></v-select>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="#6b7280" variant="text" @click="cancelSelection" class="dialog-btn">{{ $t('office.cancel') }}</v-btn>
          <v-btn color="#4f46e5" variant="elevated" @click="confirmSelection" class="dialog-btn">{{ $t('office.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Main Content -->
    <div class="modern-content">
      <!-- Building Info & Search -->
      <div class="building-info-section" v-if="selectedBuilding">
        <div class="building-header">
          <div class="building-badge">
            <v-icon color="white" size="16" class="building-icon">mdi-office-building</v-icon>
            <span class="building-name">{{ selectedBuilding }}</span>
          </div>
          <div class="search-wrapper">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              :label="$t('office.search-spaces')"
              variant="outlined"
              density="comfortable"
              class="modern-search"
              hide-details
            ></v-text-field>
          </div>
        </div>
      </div>

      <!-- Cards Grid -->
      <div class="cards-grid-modern">
        <!-- Enhanced Building Utilization Card -->
        <div class="utilization-card-wrapper">
          <div class="utilization-card" @click="goToBuildingUtilization">
            <!-- Animated Background -->
            <div class="card-bg-animation"></div>

            <!-- Live Indicator -->
            <div class="live-indicator-modern">
              <div class="pulse-dot"></div>
              <span class="live-label">{{ $t('office.live') }}</span>
            </div>

            <!-- Card Content -->
            <div class="utilization-content">
              <!-- Header -->
              <div class="utilization-header">
                <div class="icon-container">
                  <div class="icon-bg"></div>
                  <v-icon size="28" class="main-icon">mdi-chart-line</v-icon>
                </div>
                <div class="header-text">
                  <h3 class="card-title">{{ $t('office.building-utilization') }}</h3>
                  <p class="card-description">{{ $t('office.real-time-analytics') }}</p>
                </div>
              </div>

              <!-- Stats -->
              <div class="stats-container">
                <div class="stat-box">
                  <div class="stat-value">85%</div>
                  <div class="stat-label">{{ $t('office.usage-rate') }}</div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-box">
                  <div class="stat-value">142</div>
                  <div class="stat-label">{{ $t('office.active-rooms') }}</div>
                </div>
              </div>

              <!-- Progress Visualization -->
              <div class="progress-section">
                <div class="progress-bar-modern">
                  <div class="progress-fill-modern" :style="{ width: '85%' }"></div>
                </div>
                <div class="progress-labels">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>

              <!-- CTA -->
              <div class="cta-section">
                <div class="cta-text">
                  <v-icon size="16" class="trend-icon">mdi-trending-up</v-icon>
                  <span>{{ $t('office.view-detailed-analytics') }}</span>
                </div>
                <v-icon size="18" class="arrow-icon">mdi-arrow-right</v-icon>
              </div>
            </div>

            <!-- Hover Effects -->
            <div class="hover-gradient"></div>
          </div>
        </div>

        <!-- Category Cards -->
        <div v-for="poiCategory in filteredPoiCategories" :key="poiCategory.id" class="category-card-wrapper">
          <div class="category-card" @click="goToPoiCategory(poiCategory)">
            <div class="card-image-wrapper">
              <img :src="poiCategory.image" class="category-image" />
              <div class="image-overlay"></div>
            </div>

            <div class="card-content">
              <div class="content-header">
                <h4 class="category-title">{{ poiCategory.name }}</h4>
                <div class="availability-badge">
                  <div class="badge-dot"></div>
                  <span class="badge-text">{{ poiCategory.pois?.length || 0 }} {{ $t('office.available-count') }}</span>
                </div>
              </div>

              <div class="card-footer">
                <div class="room-count">
                  <v-icon size="14" color="#6b7280">mdi-door-open</v-icon>
                  <span>{{ poiCategory.pois?.length || 0 }} {{ $t('office.rooms') }}</span>
                </div>
                <div class="explore-btn">
                  <span>{{ $t('office.explore') }}</span>
                  <v-icon size="14">mdi-arrow-right</v-icon>
                </div>
              </div>
            </div>

            <!-- Hover Animation -->
            <div class="card-hover-effect"></div>
          </div>
        </div>
      </div>
    </div>

    <MapView ref="mapView" />
    <OutsideBuildingView ref="buildingView" />
  </div>
</template>

<script>
  import { defineComponent, ref, onMounted, computed, onBeforeUnmount } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { debounce, flatten, some, find, sortBy } from 'lodash';
  import { Preferences } from '@capacitor/preferences';
  import { Device } from '@capacitor/device';
  import {
    getStoredItem,
    setStoredItem
  } from '@/services/utils.js';
  import {
    getPrimaryColor,
    getToolbarLogoImage,
    getToolBarCustomerName
  } from '@/assets/Branding/branding.js';
  import {
    getBuildings,
    getPoiCategoriesByBuildingId,
    getFile,
    getDataFromBlob, 
    getAvailablePois
  } from '@/controllers/BaseController';
  import Loader from '@/components/general/Loader.vue';
  import MapView from '@/components/general/MapView.vue';
  import OutsideBuildingView from '@/components/general/ChildComponents/OutsideBuildingView.vue';
  import moment from 'moment';

  export default defineComponent({
    name: 'BuildingView',
    components: {
      Loader,
      MapView,
      OutsideBuildingView,
    },
    setup() {
      const { t } = useI18n();
      const showDialog = ref(false);
      const search = ref('');
      const logo = ref('');
      const primaryColor = ref('');
      const customerName = ref('');
      const showLoader = ref(true);
      const buildings = ref([]);
      const selectedBuilding = ref(null);
      const tempSelectedBuilding = ref(null);
      const poiCategories = ref([]);
      const isMobileView = ref(window.innerWidth <= 960);
      const mapView = ref(null);
      const buildingView = ref(null);
      const router = useRouter();

      const handleResize = () => {
        isMobileView.value = window.innerWidth <= 960;
      };

      const goToBuildingUtilization = () => {
        router.push({ name: 'BuildingUtilization' });
      };

      const filteredPoiCategories = computed(() => {
        if (!search.value) return poiCategories.value;
        const term = search.value.toLowerCase();
        return poiCategories.value.filter(cat => cat.name.toLowerCase().includes(term));
      });

      async function loadData() {
        try {
          window.addEventListener('resize', handleResize);

          const [toolbarLogo, customer, apiBuildings] = await Promise.all([
            getToolbarLogoImage(),
            getToolBarCustomerName(),
            getBuildings()
          ]);

          logo.value = toolbarLogo;
          customerName.value = customer;
          buildings.value = apiBuildings;
          primaryColor.value = await getPrimaryColor();

          const storedBuilding = await getStoredItem('selectedBuilding');
          selectedBuilding.value = storedBuilding || apiBuildings[0]?.name || null;

          if (!storedBuilding) {
            await setStoredItem('selectedBuilding', selectedBuilding.value);
          }

          const selectedBuildingId = apiBuildings.find(b => b.name === selectedBuilding.value)?.id;
          if (!selectedBuildingId) return;

          let categories = await getPoiCategoriesByBuildingId(selectedBuildingId);

          for (const category of categories) {
            const imageFile = find(category.files.entities, { type: 'image' });
            if (imageFile?.id) {
              try {
                const blob = await getFile(imageFile.id);
                category.image = await getDataFromBlob(blob);
              } catch {
                category.image = require('@/assets/CategoryImages/teamzone_iq.jpg');
              }
            } else {
              category.image = require('@/assets/CategoryImages/teamzone_iq.jpg');
            }
          }

          poiCategories.value = sortBy(categories, ['name']);
        } catch (error) {
          console.error('[BuildingView] Failed to load initial data:', error);
        } finally {
          showLoader.value = false;
        }
      }

      function confirmSelection() {
        const buildingName = tempSelectedBuilding.value?.name;
        if (buildingName) {
          selectedBuilding.value = buildingName;
          setStoredItem('selectedBuilding', buildingName);
        }
        showDialog.value = false;
        window.location.reload();
      }

      function cancelSelection() {
        tempSelectedBuilding.value = selectedBuilding.value;
        showDialog.value = false;
      }

      function goToPoiCategory(poiCategory) {
        const category = { ...poiCategory };
        const building = buildings.value.find(b => b.name === selectedBuilding.value);
        if (!building) return;

        router.push({
          name: 'PoisListView',
          params: {
            building_id: building.id,
            category_id: category.id,
          },
        });
      }

      function openDialog() {
        tempSelectedBuilding.value = selectedBuilding.value;
        showDialog.value = true;
      }

      function openMapView() {
        const building = buildings.value.find(b => b.name === selectedBuilding.value);
        if (!building) return;

        const POIS = flatten(poiCategories.value.map(cat => cat.pois || []));
        mapView.value?.openMap({ ...building }, POIS, [...poiCategories.value]);
      }

      function openBuildingView() {
        buildingView.value?.openBuilding();
      }

      function closeBuildingView() {
        buildingView.value?.closeBuilding();
      }

      onMounted(async () => {
        await loadData();
        const startDate = moment.utc('2025-08-04T14:00:00')
        const endDate = moment.utc('2025-08-04T15:00:00')
        const pois = await getAvailablePois(startDate, endDate);
        console.log('Available POIs:', pois);
      });
      
      onBeforeUnmount(() => window.removeEventListener('resize', handleResize));

      return {
        showDialog,
        search,
        logo,
        customerName,
        buildings,
        selectedBuilding,
        tempSelectedBuilding,
        showLoader,
        poiCategories,
        filteredPoiCategories,
        confirmSelection,
        goToPoiCategory,
        cancelSelection,
        openDialog,
        openMapView,
        openBuildingView,
        closeBuildingView,
        mapView,
        buildingView,
        primaryColor,
        isMobileView,
        handleResize,
        goToBuildingUtilization
      };
    },
  });
</script>

<style scoped>
  /* Modern Design System */
  .modern-building-view {
    min-height: 100vh;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%);
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Header */
  .modern-header-wrapper {
    position: sticky;
    top: 0;
    z-index: 100;
    backdrop-filter: blur(20px);
  }

  .modern-toolbar {
    background: rgba(255, 255, 255, 0.9) !important;
    border-bottom: 1px solid rgba(226, 232, 240, 0.3);
    box-shadow: 0 1px 20px rgba(15, 23, 42, 0.08);
  }

  .toolbar-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 0 auto;
    padding: 12px 20px;
  }

  .brand-section {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .logo-wrapper {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    box-shadow: 0 4px 12px rgba(79, 195, 247, 0.3);
  }

  .modern-logo {
    height: 32px;
    width: auto;
    filter: brightness(0) invert(1);
  }

  .brand-text {
    display: flex;
    flex-direction: column;
  }

  .workspace-title {
    font-size: 1.375rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    letter-spacing: -0.025em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
  }

  .workspace-subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0;
    font-weight: 500;
  }

  .action-buttons {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .modern-action-btn {
    width: 44px;
    height: 44px;
    background: rgba(79, 195, 247, 0.1);
    border-radius: 12px;
    border: 1px solid rgba(79, 195, 247, 0.2);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .modern-action-btn:hover {
    background: rgba(79, 195, 247, 0.15);
    transform: translateY(-1px);
  }

  .modern-primary-btn {
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%) !important;
    border-radius: 12px !important;
    padding: 12px 20px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.025em !important;
    box-shadow: 0 4px 12px rgba(79, 195, 247, 0.3) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: 8px !important;
  }

  .modern-primary-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(79, 195, 247, 0.4) !important;
  }

  .btn-text {
    margin-left: 0;
    font-size: 0.875rem;
  }

  /* Dialog */
  .modern-dialog .v-overlay__content {
    border-radius: 20px;
    overflow: hidden;
  }

  .dialog-card {
    border-radius: 20px !important;
    overflow: hidden;
  }

  .dialog-header {
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
    padding: 24px;
    color: white;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .dialog-icon {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .dialog-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0;
  }

  .dialog-content {
    padding: 24px !important;
  }

  .modern-select {
    margin-top: 8px;
  }

  .dialog-actions {
    padding: 16px 24px 24px !important;
  }

  .dialog-btn {
    text-transform: none !important;
    font-weight: 600 !important;
    border-radius: 10px !important;
    padding: 8px 16px !important;
  }

  /* Main Content */
  .modern-content {
    width: 100%;
    padding: 32px 20px;
  }

  /* Building Info */
  .building-info-section {
    margin-bottom: 32px;
  }

  .building-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
  }

  .building-badge {
    display: flex;
    align-items: center;
    gap: 12px;
    background: linear-gradient(135deg, #4fc3f7 0%, #2196f3 100%);
    padding: 12px 20px;
    border-radius: 16px;
    color: white;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(79, 195, 247, 0.3);
    min-width: 0;
  }

  .building-icon {
    flex-shrink: 0;
  }

  .building-name {
    font-size: 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-wrapper {
    flex: 1;
    max-width: 400px;
  }

  .modern-search .v-field {
    border-radius: 16px !important;
    background: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(10px) !important;
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.08) !important;
  }

  /* Cards Grid */
  .cards-grid-modern {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    align-items: start;
  }

  /* Utilization Card */
  .utilization-card-wrapper {
    grid-column: span 1;
  }

  .utilization-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
    border-radius: 24px;
    overflow: hidden;
    position: relative;
    height: 280px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .utilization-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 16px 48px rgba(102, 126, 234, 0.4);
  }

  .card-bg-animation {
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg, transparent 0deg 340deg, rgba(255, 255, 255, 0.1) 340deg 360deg);
    animation: rotate-animation 12s linear infinite;
    pointer-events: none;
  }

  .live-indicator-modern {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    padding: 6px 10px;
    border-radius: 20px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    z-index: 10;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    background: #00ff88;
    border-radius: 50%;
    animation: pulse-animation 2s infinite;
    box-shadow: 0 0 12px rgba(0, 255, 136, 0.6);
  }

  .live-label {
    font-size: 0.75rem;
    font-weight: 700;
    color: white;
    letter-spacing: 0.1em;
  }

  .utilization-content {
    position: relative;
    height: 100%;
    padding: 24px;
    padding-top: 20px;
    padding-right: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    z-index: 2;
    color: white;
  }

  .utilization-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 20px;
  }

  .icon-container {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .icon-bg {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  .main-icon {
    position: relative;
    z-index: 1;
    color: white;
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
  }

  .header-text {
    flex: 1;
  }

  .card-title {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 4px 0;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    line-height: 1.2;
  }

  .card-description {
    font-size: 0.875rem;
    margin: 0;
    opacity: 0.9;
    font-weight: 500;
  }

  .stats-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 16px;
    padding: 16px 20px;
    margin-bottom: 20px;
  }

  .stat-box {
    text-align: center;
    flex: 1;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 800;
    margin-bottom: 4px;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .stat-label {
    font-size: 0.75rem;
    opacity: 0.9;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stat-divider {
    width: 1px;
    height: 32px;
    background: rgba(255, 255, 255, 0.3);
    margin: 0 16px;
  }

  .progress-section {
    margin-bottom: 20px;
  }

  .progress-bar-modern {
    height: 8px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    margin-bottom: 8px;
  }

  .progress-fill-modern {
    height: 100%;
    background: linear-gradient(90deg, #00ff88 0%, #00d4ff 100%);
    border-radius: 4px;
    position: relative;
    transition: width 1.5s ease-out;
    box-shadow: 0 0 12px rgba(0, 255, 136, 0.4);
  }

  .progress-fill-modern::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 40px;
    background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.5) 50%, transparent 100%);
    animation: shimmer-animation 2.5s infinite;
  }

  .progress-labels {
    display: flex;
    justify-content: space-between;
    font-size: 0.75rem;
    opacity: 0.8;
    font-weight: 600;
  }

  .cta-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    padding: 12px 16px;
    transition: all 0.3s ease;
  }

  .utilization-card:hover .cta-section {
    background: rgba(255, 255, 255, 0.15);
    transform: translateX(4px);
  }

  .cta-text {
    font-size: 0.875rem;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .trend-icon {
    color: #00ff88;
  }

  .arrow-icon {
    transition: transform 0.3s ease;
  }

  .utilization-card:hover .arrow-icon {
    transform: translateX(4px);
  }

  .hover-gradient {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .utilization-card:hover .hover-gradient {
    opacity: 1;
  }

  /* Category Cards */
  .category-card-wrapper {
    grid-column: span 1;
  }

  .category-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.5);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
    height: 280px;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
  }

  .category-card:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 40px rgba(15, 23, 42, 0.15);
  }

  .card-image-wrapper {
    position: relative;
    height: 180px;
    overflow: hidden;
  }

  .category-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .category-card:hover .category-image {
    transform: scale(1.1);
  }

  .image-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.1) 100%);
    transition: background 0.3s ease;
  }

  .category-card:hover .image-overlay {
    background: linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.2) 100%);
  }

  .card-content {
    padding: 20px;
    height: 100px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .content-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .category-title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0;
    line-height: 1.3;
    flex: 1;
    min-width: 0;
  }

  .availability-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(16, 185, 129, 0.1);
    padding: 4px 8px;
    border-radius: 8px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .badge-dot {
    width: 6px;
    height: 6px;
    background: #10b981;
    border-radius: 50%;
    animation: pulse-animation 2s infinite;
  }

  .badge-text {
    font-size: 0.75rem;
    font-weight: 600;
    color: #059669;
  }

  .card-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  .room-count {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
  }

  .explore-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.875rem;
    font-weight: 600;
    color: #2196f3;
    transition: all 0.3s ease;
  }

  .category-card:hover .explore-btn {
    color: #1976d2;
    transform: translateX(4px);
  }

  .card-hover-effect {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(79, 195, 247, 0.05) 0%, transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  .category-card:hover .card-hover-effect {
    opacity: 1;
  }

  /* Animations */
  @keyframes rotate-animation {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes pulse-animation {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.2);
    }
  }

  @keyframes shimmer-animation {
    0% { transform: translateX(-40px); }
    100% { transform: translateX(300px); }
  }

  /* Responsive Design */

  /* 1200px breakpoint */
  @media (max-width: 1200px) {
    .modern-content {
      padding: 28px 20px;
    }

    .cards-grid-modern {
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 20px;
    }
  }

  /* 968px breakpoint */
  @media (max-width: 968px) {
    .modern-content {
      padding: 24px 16px;
    }

    .building-header {
      flex-direction: column;
      align-items: stretch;
      gap: 16px;
    }

    .search-wrapper {
      max-width: none;
    }

    .cards-grid-modern {
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
      gap: 18px;
    }

    .workspace-title {
      font-size: 1.25rem;
      max-width: 250px;
    }

    .workspace-subtitle {
      font-size: 0.8rem;
    }
  }

  /* 768px breakpoint */
  @media (max-width: 768px) {
    .modern-content {
      padding: 20px 16px;
    }

    .toolbar-content {
      padding: 8px 16px;
    }

    .brand-section {
      gap: 12px;
    }

    .logo-wrapper {
      width: 40px;
      height: 40px;
    }

    .modern-logo {
      height: 24px;
    }

    .workspace-title {
      font-size: 1.125rem;
      max-width: 200px;
    }

    .workspace-subtitle {
      font-size: 0.75rem;
    }

    .action-buttons {
      gap: 8px;
    }

    .modern-action-btn {
      width: 40px;
      height: 40px;
    }

    .modern-primary-btn {
      padding: 10px 16px !important;
    }

    .btn-text {
      font-size: 0.8rem;
    }

    .cards-grid-modern {
      grid-template-columns: 1fr;
      gap: 16px;
    }

    .utilization-card {
      height: 260px;
    }

    .utilization-content {
      padding: 20px;
      padding-right: 90px;
    }

    .card-title {
      font-size: 1.125rem;
    }

    .category-card {
      height: 260px;
    }

    .card-image-wrapper {
      height: 160px;
    }
  }

  /* 450px breakpoint */
  @media (max-width: 450px) {
    .modern-content {
      padding: 16px 12px;
    }

    .toolbar-content {
      padding: 8px 12px;
    }

    .brand-section {
      gap: 8px;
      min-width: 0;
      flex-shrink: 1;
    }

    .brand-text {
      min-width: 0;
    }

    .workspace-title {
      font-size: 1rem;
      max-width: 150px;
    }

    .workspace-subtitle {
      display: none;
    }

    .action-buttons {
      gap: 6px;
      flex-shrink: 0;
    }

    .modern-action-btn {
      width: 36px;
      height: 36px;
    }

    .modern-primary-btn {
      padding: 8px 12px !important;
    }

    .btn-text {
      display: none;
    }

    .building-badge {
      padding: 10px 16px;
      font-size: 0.875rem;
    }

    .utilization-card {
      height: 240px;
    }

    .utilization-content {
      padding: 16px;
      padding-right: 85px;
    }

    .utilization-header {
      gap: 12px;
      margin-bottom: 16px;
    }

    .icon-container {
      width: 48px;
      height: 48px;
    }

    .card-title {
      font-size: 1rem;
    }

    .card-description {
      font-size: 0.8rem;
    }

    .stats-container {
      padding: 12px 16px;
      margin-bottom: 16px;
    }

    .stat-value {
      font-size: 1.25rem;
    }

    .category-card {
      height: 240px;
    }

    .card-image-wrapper {
      height: 140px;
    }

    .card-content {
      padding: 16px;
    }

    .category-title {
      font-size: 1rem;
    }
  }

  /* 355px breakpoint */
  @media (max-width: 355px) {
    .modern-content {
      padding: 12px 8px;
    }

    .toolbar-content {
      padding: 6px 8px;
      gap: 8px;
    }

    .brand-section {
      gap: 6px;
      min-width: 0;
      flex-shrink: 1;
    }

    .logo-wrapper {
      width: 36px;
      height: 36px;
    }

    .modern-logo {
      height: 20px;
    }

    .workspace-title {
      font-size: 0.875rem;
    }

    .action-buttons {
      gap: 4px;
      flex-shrink: 0;
    }

    .modern-action-btn {
      width: 32px;
      height: 32px;
    }

    .modern-primary-btn {
      padding: 6px 8px !important;
      min-width: 32px !important;
    }

    .building-badge {
      padding: 8px 12px;
      font-size: 0.8rem;
      gap: 8px;
    }

    .building-name {
      font-size: 0.875rem;
    }

    .utilization-card {
      height: 220px;
    }

    .utilization-content {
      padding: 12px;
      padding-right: 80px;
    }

    .live-indicator-modern {
      top: 12px;
      right: 12px;
      padding: 6px 8px;
    }

    .live-label {
      font-size: 0.65rem;
    }

    .utilization-header {
      gap: 8px;
      margin-bottom: 12px;
    }

    .icon-container {
      width: 40px;
      height: 40px;
    }

    .card-title {
      font-size: 0.875rem;
    }

    .card-description {
      font-size: 0.75rem;
    }

    .stats-container {
      padding: 10px 12px;
      margin-bottom: 12px;
    }

    .stat-value {
      font-size: 1.125rem;
    }

    .stat-label {
      font-size: 0.65rem;
    }

    .category-card {
      height: 220px;
    }

    .card-image-wrapper {
      height: 120px;
    }

    .card-content {
      padding: 12px;
      height: 100px;
    }

    .category-title {
      font-size: 0.875rem;
    }

    .availability-badge {
      padding: 3px 6px;
    }

    .badge-text {
      font-size: 0.65rem;
    }

    .room-count {
      font-size: 0.75rem;
    }

    .explore-btn {
      font-size: 0.75rem;
    }
  }

  /* Accessibility */
  @media (prefers-reduced-motion: reduce) {
    .card-bg-animation,
    .pulse-dot,
    .progress-fill-modern::after,
    .badge-dot {
      animation: none;
    }

    .utilization-card,
    .category-card,
    .modern-action-btn,
    .modern-primary-btn {
      transition: none;
    }
  }

  /* High contrast mode */
  @media (prefers-contrast: high) {
    .utilization-card,
    .category-card {
      border: 2px solid #000;
    }

    .building-badge,
    .availability-badge {
      border: 1px solid #000;
    }
  }

  /* Print styles */
  @media print {
    .modern-header-wrapper,
    .action-buttons {
      display: none;
    }

    .modern-building-view {
      background: white;
    }

    .utilization-card,
    .category-card {
      box-shadow: none;
      border: 1px solid #ccc;
    }
  }
</style>